import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, Box, Button, FormLabel, Stack, Typography } from '@mui/material';
import { FormControlPageVariablesProps, ListItemConfig, ListItemValue, TagSource, VariableData } from './index';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import ListTypeWrapper from './ListItemWrapper';
import { uid } from 'tools/tools';
import showdown from 'showdown';
import { FormComponentValue } from 'components/common/form/layout/control';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { useBlendColors } from 'hooks/useDesign';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { TListType } from '../index';
import { YNValues } from 'tools/types';
import StaticInfo from 'components/common/StaticInfo';

const converter = new showdown.Converter();

const SortableItem: any = SortableElement(({ children }: { children: ReactElement }) => {
    return <>{children}</>;
});

const SortableList: any = SortableContainer(({ children }: { children: ReactElement }) => {
    return <>{children}</>;
});

function ListType({
    tag,
    controlProps,
    updateValue,
    tagIndex,
    setShowLoader,
    loadOptions,
}: {
    tag: VariableData;
    controlProps: FormControlPageVariablesProps;
    updateValue: (index: number, value: string) => void;
    tagIndex: number;
    setShowLoader: (value: boolean) => void;
    loadOptions: (
        inputValue: string,
        options: string,
        source: TagSource,
        key: string,
        multiple: YNValues,
        init?: boolean,
        index?: number,
    ) => Promise<FormComponentValue[]>;
}) {
    const { t } = useBundleTranslation(['app/editor/page/variable']);

    const containerRef = useRef<HTMLElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState<number>();
    const [isParseError, setIsParseError] = useState(false);

    const listConfig = useMemo<ListItemConfig[] | string>(() => {
        let result = '';

        if (typeof tag.additional_options == 'object') {
            result = tag.additional_options;
        } else {
            try {
                result = tag.additional_options > '' ? JSON.parse(tag.additional_options) : '';
                setIsParseError(false);
            } catch (error) {
                setIsParseError(true);
            }
        }

        return result;
    }, [tag.additional_options]);

    const buildItems = () => {
        const result: ListItemValue[] = [];

        if (tag.value > '') {
            const rawItems: ListItemValue[] | string[] = JSON.parse(tag.value);

            rawItems.forEach((rawItem) => {
                if (typeof listConfig === 'object' && typeof rawItem === 'object' && !rawItem.list_item_uid) {
                    result.push({ ...rawItem, list_item_uid: uid() });
                }

                if (typeof listConfig === 'string') {
                    result.push({
                        value_type: 'string',
                        list_item_uid: uid(),
                        value: rawItem as string,
                    });
                }
            });
        }

        return result;
    };

    const [saveChanges, setSaveChanges] = useState(false);
    const [items, setItems] = useState<ListItemValue[]>(buildItems());

    const getItemValue = (item: ListItemValue) => {
        if (item.hasOwnProperty('value_type') && item.value_type === 'string') {
            return item.value;
        }

        const { list_item_uid: _, ...newItem } = item as ListItemValue;

        return newItem;
    };

    useEffect(() => {
        if (saveChanges) {
            updateValue(tagIndex, JSON.stringify(items.map((item) => getItemValue(item))));
        }
    }, [items]);

    const createNewListItem = () => {
        const newItem: ListItemValue = {
            list_item_uid: uid(),
        };
        const newItems = items.slice();

        if (typeof listConfig === 'object') {
            listConfig.forEach((config) => {
                if (typeof config === 'string') {
                    newItem[config] = '';
                }
                if (typeof config === 'object') {
                    newItem[config.name] = '';
                }
            });

            newItems.push(newItem);
        } else {
            newItems.push({
                value_type: 'string',
                list_item_uid: uid(),
                value: '',
            });
        }

        setSaveChanges(true);
        setItems(newItems);
    };

    const deleteListItem = (index: number) => {
        setDeleteIndex(index);
        setShowDeleteConfirm(true);
    };

    const deleteAction = () => {
        setShowDeleteConfirm(false);
        setSaveChanges(true);
        setItems(
            items.filter((item, itemIndex) => {
                return itemIndex !== deleteIndex;
            }),
        );
    };

    const updateListItemValue = (itemIndex: number, valueName: string, value: string) => {
        const newItems = items.slice();

        if (newItems[itemIndex].hasOwnProperty('value_type') && newItems[itemIndex].value_type === 'string') {
            newItems[itemIndex].value = value;
        } else {
            newItems[itemIndex][valueName] = value;
        }

        setSaveChanges(true);
        setItems(newItems);
    };

    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        if (containerRef?.current) {
            document.body.classList.remove('isDragActive', 'isDragActive--row');
        }
        const placeholderElement = placeholderRef?.current;
        if (placeholderElement) {
            placeholderElement.style.display = 'none';
        }

        setItems(arrayMoveImmutable(items, oldIndex, newIndex));
        setSaveChanges(true);
    };

    const deleteConfirmSettings: PopupSettings = {
        title: t('delete_confirmation_header'),
        textOK: t('delete_confirmation_button'),
        textCancel: t('delete_cancel_button'),
    };

    return (
        <Box
            ref={containerRef}
            key={tag.uid + '-list'}
            className={'sort-list-holder'}
            sx={{
                'body.isDragActive &': {
                    userSelect: 'none',
                },
            }}
        >
            <FormLabel sx={{ display: 'flex' }}>{tag.name}</FormLabel>
            {isParseError ? (
                <StaticInfo label={t('parsing_error')} align="start" type={'error'} />
            ) : (
                items.length > 0 && (
                    <SortableList
                        useDragHandle
                        onSortEnd={onSortEnd}
                        onSortStart={({ node }: { node: any }) => {
                            const containerElement = containerRef?.current;
                            const placeholderElement = placeholderRef?.current;
                            const size = node.getBoundingClientRect();
                            if (containerElement) {
                                document.body.classList.add('isDragActive', 'isDragActive--row');
                                if (placeholderElement) {
                                    placeholderElement.style.width = size.width + 'px';
                                    placeholderElement.style.height = size.height + 'px';
                                    placeholderElement.style.top = node.offsetTop + 'px';
                                    placeholderElement.style.display = 'block';
                                }
                            }
                        }}
                    >
                        <Stack
                            key={tag.uid + '-list-items'}
                            sx={{ position: 'relative', mt: -1 }}
                            className={'sort-list-holder-inner'}
                        >
                            <Stack
                                ref={placeholderRef}
                                sx={{
                                    position: 'absolute',
                                    display: 'none',
                                    pt: 1,
                                }}
                            >
                                <Box
                                    className={'drag-and-drop-placeholder'}
                                    sx={{
                                        border: '1px dashed',
                                        borderColor: (theme) => useBlendColors(alpha(theme.palette.text.primary, 0.16)),
                                        borderRadius: 1,
                                        width: '100%',
                                        height: '100%',
                                    }}
                                />
                            </Stack>
                            {items.map((item, index) => (
                                <SortableItem index={index} key={tag.uid + '-' + item.list_item_uid}>
                                    <ListTypeWrapper
                                        key={tag.uid + '-ss-' + item.list_item_uid}
                                        listConfig={listConfig}
                                        uid={tag.uid}
                                        deleteListItem={deleteListItem}
                                        updateListItemValue={updateListItemValue}
                                        setShowLoader={setShowLoader}
                                        loadOptions={loadOptions}
                                        controlProps={controlProps}
                                        index={index}
                                        item={item}
                                        tag={tag}
                                    />
                                </SortableItem>
                            ))}
                        </Stack>
                    </SortableList>
                )
            )}
            {!isParseError && (
                <Stack direction={'row'} alignItems={'center'} sx={{ mt: 1 }}>
                    <Button
                        variant="light"
                        startIcon={<IconMi icon={'new'} />}
                        sx={{ flexShrink: 0 }}
                        onClick={createNewListItem}
                    >
                        {t('create_variable')}
                    </Button>
                    {tag.description && (
                        <Box sx={{ ml: 1 }}>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: converter.makeHtml(tag.description),
                                }}
                            />
                        </Box>
                    )}
                </Stack>
            )}
            {showDeleteConfirm && (
                <Popup
                    key={'variable-delete-confirm'}
                    settings={deleteConfirmSettings}
                    open={showDeleteConfirm}
                    onHide={() => setShowDeleteConfirm(false)}
                    onConfirm={deleteAction}
                    maxWidth={'popupSm'}
                >
                    <Typography>{t('delete_confirmation')}</Typography>
                </Popup>
            )}
        </Box>
    );
}

export default ListType;
